import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import FileDropzone from '@components/FileDropzone/FileDropzone';
import InputLabel from '@components/InputLabel';
import { Progress } from '@components/Progress';

import { useUploadFile } from '@hooks/api/file';
import { useMultiUploadProgress } from '@hooks/useMultiUploadProgress';
import { useSession } from '@hooks/useSession';
import { useSnackbar } from '@hooks/useSnackbar';

import type { FhirResource } from '@models/fhir-resource';

import type { Attachment as AttachmentType } from 'generated/types';

import AttachmentList from './AttachmentList';

const messages = defineMessages({
  attachments: {
    id: 'attachments.attachments',
    defaultMessage: 'Attachments',
  },
  postError: {
    id: 'attachments.postError',
    defaultMessage: 'Failed to upload attachment(s)',
  },
  noRecordError: {
    id: 'attachments.noRecordError',
    defaultMessage: 'There is no record to attach files to',
  },
});

interface Props {
  /** Title of component (defaulted to 'Attachments') */
  title?: string;
  /** Attachments of the resource  */
  attachments?: AttachmentType[];
  /** If no resource exists: Callback that fires before attachments are uploaded */
  onBeforeUpload?: () => Promise<FhirResource | null>;
  /** Existing resource: <resourceType>/<resourceId> */
  resource?: string;
}

/**
 * Component used to create resource and then attach file(s) to it
 */
export default function PostRecordAndAttach({
  attachments = [],
  onBeforeUpload,
  title,
  resource,
}: Props) {
  const { formatMessage } = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const {
    state: { activeProfile, isCurator },
  } = useSession();
  const [patient_id = ''] = [activeProfile?.patient_id];

  const [{ uploadAttachment }] = useUploadFile();
  const {
    progress,
    setNumFiles,
    setUploadProgress,
    setUploadedFiles,
    setChunkSize,
    reset,
  } = useMultiUploadProgress();

  const handleBeforeUpload = async () => onBeforeUpload?.();

  const handleUpload = async (files: DroppedFile[]) => {
    setNumFiles(files.length);
    setChunkSize(1);
    // 1. Create FHIR resource
    let record = resource;
    if (!resource) {
      const createdResource = await handleBeforeUpload();
      record = createdResource?.getResourceId();
    }
    if (!record) {
      enqueueSnackbar({
        message: formatMessage(messages.noRecordError),
        variant: 'error',
      });
      return;
    }

    for (const file of files) {
      // 2. Attach files to FHIR resource
      try {
        await uploadAttachment(record, file, (progress) =>
          setUploadProgress(progress)
        );
      } catch (err) {
        enqueueSnackbar({
          message: formatMessage(messages.postError),
          variant: 'error',
        });
      } finally {
        setUploadedFiles((prev) => prev + 1);
        setUploadProgress(0);
      }
    }
    reset();
  };

  return (
    <>
      <InputLabel
        label={title || formatMessage(messages.attachments)}
        style={{ color: 'primary.main' }}
      />
      <AttachmentList
        attachments={attachments}
        can_delete={isCurator}
        patient_id={patient_id}
        resource={resource}
      />
      <FileDropzone onUpload={handleUpload} />
      {typeof progress === 'number' ? (
        <Progress value={progress} width="100%" />
      ) : null}
    </>
  );
}
